import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { daysBetween } from '@pace/domain';
import { Button, Empty, Icon, PartThumb, Spinner } from '@pace/ui';
import { useStepAccess, useStepActions } from '../components/actions';
import { PageHead, Shell } from '../components/Shell';
import { DoneSheet, HoldSheet, StepSheet, toolName } from '../components/sheets';
import { Slip, StepChip } from '../components/status';
import { useLookups, type Lookups } from '../data/bootstrap';
import { useProjects, useViews } from '../data/projects';
import { useI18n } from '../i18n';
import { fmtDay } from '../lib/time';
import { useAuthed } from '../session';
import { myWork, type WorkItem } from './work';

type Tab = 'todo' | 'coming' | 'done';
type SheetState = { kind: 'done' | 'hold' | 'step'; item: WorkItem } | null;

function WorkCard({
  item,
  lk,
  mode,
  focused,
  onSheet,
}: {
  item: WorkItem;
  lk: Lookups;
  mode: Tab;
  focused?: boolean;
  onSheet: (s: SheetState) => void;
}) {
  const { t, lang } = useI18n();
  const actions = useStepActions();
  const access = useStepAccess()(item.project, item.step);
  const ref = useRef<HTMLElement>(null);
  const { project: p, step: s, view: v } = item;
  const today = lk.today();
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [focused]);

  const when = (() => {
    if (s.state === 'done') return t('work.doneDate', { date: fmtDay(s.actual, lang) });
    const d = v.ref ? daysBetween(today, v.ref) : null;
    if (d == null) return '';
    if (d < 0) return t('work.lateBy', { n: -d });
    if (d === 0) return t('common.today');
    if (d === 1) return t('work.tomorrow');
    return t('work.daysLeft', { n: d });
  })();

  let tail: React.ReactNode = null;
  if (access.access !== 'none' && mode !== 'done') {
    tail =
      s.state === 'hold' ? (
        <div className="acts acts--2">
          <button type="button" className="act act--ok" onClick={() => actions.done(p, s, today)}>
            <Icon name="check" size={20} />
            {t('work.doneToday')}
            <kbd>D</kbd>
          </button>
          <button type="button" className="act act--part" onClick={() => actions.resume(p, s)}>
            <Icon name="play" size={18} />
            {t('work.resume')}
          </button>
        </div>
      ) : (
        <div className="acts">
          <button type="button" className="act act--ok" onClick={() => actions.done(p, s, today)}>
            <Icon name="check" size={20} />
            {t('work.doneToday')}
            <kbd>D</kbd>
          </button>
          <button type="button" className="act act--part" onClick={() => onSheet({ kind: 'done', item })}>
            <Icon name="calendar" size={18} />
            {t('work.doneOn')}
          </button>
          <button type="button" className="act act--bad" onClick={() => onSheet({ kind: 'hold', item })}>
            <Icon name="stop" size={18} />
            {t('work.hold')}
            <kbd>H</kbd>
          </button>
        </div>
      );
  } else if (mode === 'done') {
    tail = (
      <div className="card__status">
        <span className="txt">
          {fmtDay(s.actual, lang, true)} · {lk.userName(s.updatedBy)}
        </span>
        {access.access !== 'none' && (
          <Button size="sm" onClick={() => onSheet({ kind: 'step', item })}>
            {t('common.change')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <article ref={ref} className={`card${focused ? ' card--focus' : ''}`} aria-current={focused || undefined}>
      <button
        type="button"
        className="card__head card__head--btn"
        onClick={() => onSheet({ kind: 'step', item })}
      >
        <PartThumb name={p.partName} photoUrl={p.photoUrl} />
        <div>
          <div className="eyebrow">{lk.stageName(s)}</div>
          <div className="card__name">{p.partName}</div>
          <div className="card__meta">
            {toolName(p, s, t('project.milestones'))} · {p.code}
          </div>
        </div>
        <div className="card__qty">
          <b>{fmtDay(s.state === 'done' ? s.actual : v.ref, lang)}</b>
          <span>{when}</span>
        </div>
      </button>
      <div className="card__tags">
        <StepChip status={v.status} />
        {s.state !== 'done' && <Slip days={v.slip} />}
        {s.state === 'hold' && <span className="txt">{lk.reasonLabel(s.holdReasonId)}</span>}
        {s.state !== 'done' && !v.ready && item.prev && (
          <span className="muted">{t('work.waitingFor', { stage: lk.stageName(item.prev) })}</span>
        )}
        {s.state !== 'done' && v.forecast && v.ref && v.forecast > v.ref && (
          <span className="muted">{t('work.forecast', { date: fmtDay(v.forecast, lang) })}</span>
        )}
        {s.photos > 0 && (
          <span className="muted">
            <Icon name="camera" size={14} /> {s.photos}
          </span>
        )}
      </div>
      {tail}
    </article>
  );
}

function Group({
  title,
  items,
  lk,
  mode,
  focusId,
  onSheet,
  tone,
}: {
  title: string;
  items: WorkItem[];
  lk: Lookups;
  mode: Tab;
  focusId?: string | null;
  onSheet: (s: SheetState) => void;
  tone?: 'hold' | 'wait';
}) {
  if (!items.length) return null;
  return (
    <section className="stack">
      <p className={`eyebrow${tone ? ` eyebrow--${tone}` : ''}`}>
        {title} · {items.length}
      </p>
      <div className="cards cards--2">
        {items.map((i) => (
          <WorkCard
            key={i.step.id}
            item={i}
            lk={lk}
            mode={mode}
            focused={focusId === i.step.id}
            onSheet={onSheet}
          />
        ))}
      </div>
    </section>
  );
}

/** Stage owner's home: what is due in my stages, one tap to mark it done. */
export default function WorkPage() {
  const { t } = useI18n();
  const { tab = 'todo' } = useParams();
  const session = useAuthed();
  const lk = useLookups();
  const projects = useProjects();
  const views = useViews(projects.data, lk);
  const actions = useStepActions();
  const [sheet, setSheet] = useState<SheetState>(null);
  const [dept, setDept] = useState<string | null>(null);
  const [focus, setFocus] = useState(0);

  const w = useMemo(
    () => (lk && projects.data ? myWork(projects.data, views, lk, session.user.id, dept) : null),
    [lk, projects.data, views, session.user.id, dept],
  );
  const todo = useMemo(() => (w ? [...w.overdue, ...w.today, ...w.hold, ...w.week] : []), [w]);
  const focusItem = todo[Math.min(focus, Math.max(0, todo.length - 1))];

  // Laptop keyboard: ↑ ↓ move between cards, D = done today, H = hold.
  useEffect(() => {
    if (tab !== 'todo' || sheet || !lk) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      const k = e.key.toLowerCase();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocus((f) => Math.min(f + 1, todo.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocus((f) => Math.max(f - 1, 0));
      } else if (k === 'd' && focusItem && focusItem.step.state !== 'hold')
        actions.done(focusItem.project, focusItem.step, lk.today());
      else if (k === 'h' && focusItem && focusItem.step.state === 'open')
        setSheet({ kind: 'hold', item: focusItem });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tab, sheet, lk, todo, focusItem, actions]);

  if (!['todo', 'coming', 'done'].includes(tab)) return <Navigate to="/work" replace />;
  const title = t('work.title');
  if (!lk || !w) {
    return (
      <Shell title={title}>
        <Spinner label={t('common.loading')} />
      </Shell>
    );
  }
  const myDepts = lk.user(session.user.id)?.departmentIds ?? [];
  const mode = tab as Tab;
  const common = { lk, mode, onSheet: setSheet, focusId: mode === 'todo' ? focusItem?.step.id : null };

  let body: React.ReactNode;
  if (!myDepts.length) body = <Empty title={t('work.allClear')}>{t('work.noDepartments')}</Empty>;
  else if (mode === 'todo') {
    body = todo.length ? (
      <>
        <p className="muted kbd-hint">{t('work.keyboardHint')}</p>
        <Group title={t('work.overdue')} items={w.overdue} tone="hold" {...common} />
        <Group title={t('work.dueToday')} items={w.today} tone="wait" {...common} />
        <Group title={t('work.onHold')} items={w.hold} tone="hold" {...common} />
        <Group title={t('work.thisWeek')} items={w.week} {...common} />
      </>
    ) : (
      <Empty title={t('work.allClear')}>{t('work.noTodo')}</Empty>
    );
  } else if (mode === 'coming') {
    body = w.later.length ? (
      <Group title={t('work.later')} items={w.later} {...common} />
    ) : (
      <Empty title={t('work.allClear')}>{t('work.noComing')}</Empty>
    );
  } else {
    body = w.done.length ? (
      <Group title={t('work.done')} items={w.done} {...common} />
    ) : (
      <Empty title={t('work.done')}>{t('work.noDone')}</Empty>
    );
  }

  const waiting = w.overdue.length + w.today.length;
  return (
    <Shell title={title} waiting={waiting}>
      <PageHead title={title} sub={myDepts.map((d) => lk.deptName(d)).join(' · ')} />
      {myDepts.length > 1 && (
        <nav className="scroller" aria-label={t('work.myDepartments')}>
          <div className="seg">
            <button type="button" aria-pressed={!dept} onClick={() => setDept(null)}>
              {t('common.all')}
            </button>
            {myDepts.map((d) => (
              <button key={d} type="button" aria-pressed={dept === d} onClick={() => setDept(d)}>
                {lk.deptName(d)}
              </button>
            ))}
          </div>
        </nav>
      )}
      {body}
      {sheet?.kind === 'done' && (
        <DoneSheet
          project={sheet.item.project}
          step={sheet.item.step}
          lk={lk}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet?.kind === 'hold' && (
        <HoldSheet
          project={sheet.item.project}
          step={sheet.item.step}
          lk={lk}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet?.kind === 'step' && (
        <StepSheet
          project={projects.data?.find((p) => p.id === sheet.item.project.id) ?? sheet.item.project}
          step={
            projects.data
              ?.find((p) => p.id === sheet.item.project.id)
              ?.steps.find((x) => x.id === sheet.item.step.id) ?? sheet.item.step
          }
          lk={lk}
          onClose={() => setSheet(null)}
        />
      )}
    </Shell>
  );
}
